"use client";

import { useDynamicContext } from "@dynamic-labs/sdk-react-core";
import { useMutation, useQuery } from "@tanstack/react-query";
import React from "react";
import { formatUnits } from "viem";
import { useWalletClient } from "wagmi";
import {
  BERACHAIN_CHAIN_ID,
  fromHaikuIID,
  type HaikuToken,
  HENLO_TOKEN_ADDRESS,
  toHaikuIID,
} from "@/lib/services/haiku-swap-service";

export function useHaikuTokens(
  chainId?: number,
  userAddress?: string,
  page: number = 0,
  pageSize: number = 100,
) {
  return useQuery({
    queryKey: ["haiku-tokens", chainId, userAddress, page, pageSize],
    queryFn: async () => {
      const response = await fetch("/api/haiku/tokens", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ chainId, userAddress, page, pageSize }),
      });

      if (!response.ok) throw new Error("Failed to fetch tokens");
      const result = await response.json();

      const tokens = Array.isArray(result) ? result : ((result.tokens || []) as HaikuToken[]);
      const hasMore = !Array.isArray(result) ? result.hasMore : false;
      const totalCount = !Array.isArray(result) ? result.totalCount : tokens.length;

      if (userAddress && tokens.length > 0) {
        try {
          const balanceResponse = await fetch("/api/haiku/balances", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ userAddress, chainId }),
          });

          if (balanceResponse.ok) {
            const balanceData = await balanceResponse.json();

            const tokensWithBalance = tokens.map((token) => {
              const tokenChainId = token.chainId || chainId;
              const tokenIID = toHaikuIID(tokenChainId, token.address);

              const balance = balanceData.wallet_positions?.[tokenIID];
              const priceInfo = balanceData.prices?.[tokenIID];
              const price = typeof priceInfo === "string" ? priceInfo : priceInfo?.price;

              let valueUsd = 0;
              if (balance && price) {
                const balanceNum = parseFloat(balance);
                const priceNum = parseFloat(price);
                valueUsd = balanceNum * priceNum;
              }

              return {
                ...token,
                balance: balance || "0",
                valueUsd: valueUsd,
                priceUsd: price ? parseFloat(price) : undefined,
              };
            });

            return {
              tokens: tokensWithBalance,
              hasMore,
              totalCount,
              page,
            };
          }
        } catch (_error) {}
      }

      return {
        tokens: tokens,
        hasMore,
        totalCount,
        page,
      };
    },
    enabled: !!chainId,
    staleTime: 30 * 1000,
    refetchInterval: userAddress ? 60 * 1000 : undefined,
  });
}

export function useHaikuPrice(params?: {
  chainId?: number;
  tokenAddress?: string;
  symbol?: string;
}) {
  const chainId = params?.chainId ?? BERACHAIN_CHAIN_ID;
  const symbol = params?.symbol;
  const effectiveTokenAddress =
    params?.tokenAddress !== undefined
      ? params.tokenAddress
      : symbol
        ? undefined
        : HENLO_TOKEN_ADDRESS;

  const queryKeyPayload = {
    chainId,
    lookupBy: effectiveTokenAddress ? "address" : symbol ? "symbol" : "default",
    tokenAddress: effectiveTokenAddress ? effectiveTokenAddress.toLowerCase() : null,
    symbol: symbol ? symbol.toUpperCase() : null,
  } as const;

  return useQuery({
    queryKey: ["haiku-price", queryKeyPayload],
    queryFn: async () => {
      const response = await fetch("/api/haiku/price", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          chainId,
          tokenAddress: effectiveTokenAddress,
          symbol,
        }),
      });
      if (!response.ok) throw new Error("Failed to fetch token price");
      return response.json() as Promise<{
        chainId: number;
        priceUSD?: number;
        token: any;
        raw: any;
      }>;
    },
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
  });
}

// Popular/default tokens for display when wallet is not connected
export function useHaikuPopularTokens() {
  return useQuery({
    queryKey: ["haiku-popular-tokens"],
    queryFn: async () => {
      const popularChains = [1, 8453, 42161];
      const allTokens: HaikuToken[] = [];

      // Fetch in parallel to reduce perceived latency when not connected
      const results = await Promise.all(
        popularChains.map(async (chainId) => {
          try {
            const response = await fetch("/api/haiku/tokens", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ chainId, page: 0, pageSize: 20 }),
            });
            if (!response.ok) return [] as HaikuToken[];
            const result = await response.json();
            const tokens = Array.isArray(result) ? result : result.tokens || [];
            return tokens.slice(0, 10) as HaikuToken[];
          } catch (_error) {
            return [] as HaikuToken[];
          }
        }),
      );
      results.forEach((arr) => allTokens.push(...arr));
      const uniqueTokens = allTokens.reduce(
        (acc, token) => {
          const key = `${token.symbol.toLowerCase()}`;
          if (!acc[key] || acc[key].marketCap < token.marketCap) {
            acc[key] = token;
          }
          return acc;
        },
        {} as Record<string, HaikuToken>,
      );
      return Object.values(uniqueTokens)
        .sort((a, b) => (b.marketCap || 0) - (a.marketCap || 0))
        .slice(0, 20);
    },
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

export type AggregatedTokenData = {
  symbol: string;
  name: string;
  totalBalance: string;
  totalValueUSD: number;
  chains: Array<{
    chainId: number;
    chainName: string;
    balance: string;
    balanceFormatted: string;
    address: string;
    valueUSD?: number;
  }>;
  logoURI?: string;
  priceUSD?: number;
  decimals: number;
};

export function useHaikuBalances(userAddress?: string) {
  return useQuery({
    queryKey: ["haiku-balances", userAddress],
    queryFn: async () => {
      if (!userAddress) {
        return {
          wallet_positions: {},
          prices: {},
          categorised_wallet_positions: {},
        };
      }
      const response = await fetch("/api/haiku/balances", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userAddress }),
      });

      if (!response.ok) {
        throw new Error("Failed to fetch balances");
      }

      return response.json();
    },
    enabled: !!userAddress,
    staleTime: 30 * 1000,
  });
}

export function useHaikuMultiChainData(userAddress?: string) {
  const {
    data: balanceData,
    isLoading: balanceLoading,
    error: balanceError,
  } = useHaikuBalances(userAddress);

  return useQuery({
    queryKey: ["haiku-multi-chain", balanceData, userAddress],
    queryFn: async () => {
      if (!balanceData || !userAddress) {
        return { tokenBalances: [], aggregatedTokens: [] };
      }

      const response = await fetch("/api/haiku/aggregate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userAddress,
          balanceData,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to aggregate multi-chain data");
      }

      return response.json();
    },
    enabled:
      !!balanceData && !!userAddress && Object.keys(balanceData.wallet_positions || {}).length > 0,
    staleTime: 30 * 1000,
  });
}

export function useHaikuSwapQuote(params: {
  fromChainId?: number;
  fromTokenAddress?: string;
  amount?: string;
  userAddress?: string;
}) {
  const { fromChainId, fromTokenAddress, amount, userAddress } = params;
  const estimatedTime = fromChainId === BERACHAIN_CHAIN_ID ? 15 : 60;
  const estimatedFees = "0.001";
  const { data: quote, ...queryResult } = useQuery({
    queryKey: ["haiku-quote", fromChainId, fromTokenAddress, amount, userAddress],
    queryFn: async () => {
      if (!fromChainId || !fromTokenAddress || !amount) {
        return null;
      }
      const response = await fetch("/api/haiku/quote", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ fromChainId, fromTokenAddress, amount, userAddress }),
      });
      if (!response.ok) throw new Error("Failed to get quote");
      const data = await response.json();
      return data as any;
    },
    enabled: !!(fromChainId && fromTokenAddress && amount && parseFloat(amount) > 0),
    staleTime: 10 * 1000,
    refetchInterval: 15 * 1000,
  });

  const toAmount =
    (quote as any)?.outputAmount || (quote as any)?.balances?.[0]?.amount || undefined;
  return { quote, ...queryResult, estimatedTime, estimatedFees, toAmount, executeQuote: () => {} };
}

export function useHaikuSwapExecution() {
  const { data: walletClient } = useWalletClient();
  return useMutation({
    mutationFn: async (params: {
      quoteId: string;
      permit2Signature?: string;
      userSignature?: string;
      executionChainId?: number;
      approvals?: Array<{ to: string; data: `0x${string}`; value?: string | bigint | number }>;
    }) => {
      if (params.approvals?.length) {
        if (!walletClient) throw new Error("Wallet not connected for approvals");
        for (const approval of params.approvals) {
          await walletClient.sendTransaction({
            to: approval.to as `0x${string}`,
            data: approval.data as `0x${string}`,
            value: approval.value ? BigInt(approval.value as any) : 0n,
            chainId: params.executionChainId ? { id: params.executionChainId } : undefined,
          });
        }
      }
      const solveResponse = await fetch("/api/haiku/solve", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          quoteId: params.quoteId,
          permit2Signature: params.permit2Signature,
          userSignature: params.userSignature,
          expectedChainId: params.executionChainId,
        }),
      });
      if (!solveResponse.ok) throw new Error("Failed to solve intent");
      const { to, data, value, chainId } = await solveResponse.json();
      if (!walletClient) throw new Error("Wallet not connected");
      const hash = await walletClient.sendTransaction({
        to: to as `0x${string}`,
        data: data as `0x${string}`,
        value: BigInt(value || "0"),
        chain: chainId
          ? { id: chainId }
          : params.executionChainId
            ? { id: params.executionChainId }
            : (undefined as any),
      });
      return { hash, to, data, value };
    },
  });
}

export function useHaikuAggregatedData() {
  const { primaryWallet } = useDynamicContext();
  const userAddress = primaryWallet?.address;
  const multiChainData = useHaikuMultiChainData(userAddress);
  const balances = useHaikuBalances(userAddress);
  const processedData = React.useMemo(() => {
    if (multiChainData.data?.aggregatedTokens) {
      return {
        tokenBalances: multiChainData.data.tokenBalances || multiChainData.data.aggregatedTokens,
        aggregatedTokens: multiChainData.data.aggregatedTokens,
        totalValueUSD: multiChainData.data.totalValueUSD || 0,
        isLoading: multiChainData.isLoading,
      };
    }
    if (!balances.data) {
      return {
        tokenBalances: [],
        aggregatedTokens: [],
        totalValueUSD: 0,
        isLoading: multiChainData.isLoading || balances.isLoading,
      };
    }
    const aggregatedBySymbol: Record<string, AggregatedTokenData> = {};
    const { wallet_positions, prices } = balances.data;
    Object.entries(wallet_positions || {}).forEach(([iid, balance]: [string, any]) => {
      const { chainId, address } = fromHaikuIID(iid);
      const priceData = prices[iid];
      if (!priceData || parseFloat(balance) === 0) return;
      const symbol = priceData.symbol || "UNKNOWN";
      const valueUSD = parseFloat(balance) * parseFloat(priceData.price || 0);
      if (!aggregatedBySymbol[symbol]) {
        aggregatedBySymbol[symbol] = {
          symbol,
          name: priceData.name || symbol,
          totalBalance: "0",
          totalValueUSD: 0,
          chains: [],
          logoURI: priceData.logoURI,
          priceUSD: parseFloat(priceData.price || 0),
          decimals: priceData.decimals || 18,
        };
      }
      aggregatedBySymbol[symbol].totalValueUSD += valueUSD;
      aggregatedBySymbol[symbol].totalBalance = (
        parseFloat(aggregatedBySymbol[symbol].totalBalance) + parseFloat(balance)
      ).toString();
      let balanceFormatted: string;
      try {
        if (typeof balance === "string" && balance.includes(".")) {
          balanceFormatted = balance;
        } else {
          const balanceBigInt =
            typeof balance === "string" ? BigInt(balance) : BigInt(balance.toString());
          balanceFormatted = formatUnits(balanceBigInt, priceData.decimals || 18);
        }
      } catch (_error) {
        balanceFormatted = balance.toString();
      }
      aggregatedBySymbol[symbol].chains.push({
        chainId,
        chainName: getChainName(chainId),
        balance: balance,
        balanceFormatted,
        address,
        valueUSD,
      });
    });
    const aggregatedTokens = Object.values(aggregatedBySymbol).sort(
      (a, b) => b.totalValueUSD - a.totalValueUSD,
    );
    const totalValueUSD = aggregatedTokens.reduce((sum, token) => sum + token.totalValueUSD, 0);
    return { tokenBalances: aggregatedTokens, aggregatedTokens, totalValueUSD, isLoading: false };
  }, [multiChainData.data, balances.data, balances.isLoading, multiChainData.isLoading]);
  return processedData;
}

function getChainName(chainId: number): string {
  const chainNames: Record<number, string> = {
    1: "Ethereum",
    42161: "Arbitrum",
    8453: "Base",
    80094: "Berachain",
    56: "BSC",
    137: "Polygon",
    10: "Optimism",
    43114: "Avalanche",
  };
  return chainNames[chainId] || `Chain ${chainId}`;
}
